<template>
    <div>
        <base-spinner v-if="isLoading"/>
        <base-page v-else-if="patient !== null">
            <b-row class="justify-content-center" >
                <b-col cols="9" class="bg-white">
                    <b-row class="justify-content-end mr-3 my-3">
                        <b-link @click="$bvModal.show('cancel-dialog')">{{ $t('utils.cancel') }} </b-link>
                    </b-row>

                    <div class="mb-3">
                        <h2 class="text-center text-secondary">{{ pageTitle }}</h2>
                        <h5 class="text-center text-primary">{{ centerName }}</h5>
                    </div>

                    <component
                        class="mx-3"
                        :is="componentList[currentComponent]"
                        ref="child"
                    />

                    <general-error :error="error"/>

                    <step-navigation
                        :steps="componentList.length"
                        :should-go-next="next"
                        :should-go-back="back"
                        :save="save"
                        @current="componentChanged"
                    />
                </b-col>

            </b-row>

            <exit-confirm-dialog modal-id="cancel-dialog"/>
        </base-page>

        <p class="pt-3 px-3" v-else>
            {{ $t('error.notFound') }}
        </p>
    </div>
</template>

<script>
    import PatientGeneralData from "@/components/patient/PatientGeneralData";
    import PatientDisease from "@/components/patient/PatientDisease";
    import PatientTreatment from "@/components/patient/PatientTreatment";
    import PatientPreviousSurgery from "@/components/patient/PatientPreviousSurgery";
    import BaseSpinner from "@/components/BaseSpinner";
    import StepNavigation from "@/components/StepNavigation";
    import {mapActions, mapState} from "vuex";
    import {isNull} from "@/utils/formatting";
    import CreateEmptyObject from "@/components/mixins/CreateEmptyObject";
    import {mapStateProperty} from "@/utils/VuexHelpers";
    import GeneralError from "@/components/GeneralError";
    import ExitConfirmDialog from "@/components/ExitConfirmDialog";
    import BasePageMixin from "@/components/BasePageMixin";
    import BasePage from "@/components/BasePage";
    import PatientRelevantComorbidities from "@/components/patient/PatientRelevantComorbidities";
    import {logRequestError} from "@/utils/utils";

    export default {
        name: "PatientAddPage",
        components: {
            BasePage,
            ExitConfirmDialog,
            GeneralError,
            StepNavigation,
            BaseSpinner,
        },
        mixins: [CreateEmptyObject, BasePageMixin],
        props: {
            patientId: {
                type: Number,
                default: null
            },
        },
        data() {
            return {
                componentList: [
                    PatientGeneralData,
                    PatientDisease,
                    PatientTreatment,
                    PatientPreviousSurgery,
                    PatientRelevantComorbidities,
                ],
                currentComponent: 0,
                pageTitle: this.$t('patient.addPatient'),
                error: false,
            };
        },
        computed: {
            ...mapState(['patient', 'center', 'currentUser', 'patientFields', 'previousSurgeryFields']),
            ...mapStateProperty([
                {path: 'patient.center', name: 'patientCenter'},
            ]),
            centerName() {
                return this.center === null ? {name: ''} : this.center.name;
            }
        },
        async created() {
            try {
                await this.getPatientOptions();
                await this.getPreviousSurgeryOptions();
                if (!isNull(this.patientId)) {
                    await this.getPatient(this.patientId);
                    this.pageTitle = this.$t('patient.patient') + ' ' + this.patient.code;
                } else {
                    this.setPatient(this.createEmptyObjectFromFields(this.patientFields));
                }
                await this.getCenter(this.currentUser.center);
                this.patientCenter = this.center;
            } catch (error) {
                if (error.response.status !== 404) {
                    throw error;
                }
            }
            this.isLoading = false;
        },
        destroyed() {
            this.setPatient(null);
        },
        methods: {
            ...mapActions([
                'setPatient',
                'updateOrCreatePatient',
                'getPatient',
                'getPatientOptions',
                'createPreviousSurgery',
                'getPreviousSurgeryOptions',
                'getCenter',
            ]),
            async save() {
                this.error = false;
                let isValid = this.$refs.child.validate();
                if (isValid) {
                    this.updateOrCreatePatient(this.patient)
                        .then(patient => {
                            this.setPatient(null);
                            this.$router.replace({
                                name: 'patient-detail-surgeries',
                                params: {patientId: patient.id},
                            });
                        })
                        .catch(error => {
                            logRequestError(error);
                            this.error = true;
                        });
                }
                return isValid;
            },
            next() {
                return this.$refs.child.validate();
            },
            back() {
                return true;
            },
            componentChanged(index) {
                this.currentComponent = index;
            }
        }
    };
</script>

<style scoped>
</style>